import ListSearchBar from "../components/ListSearchBar";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Table, Offcanvas } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { PropagateLoader } from "react-spinners";
import { MapContainer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BaseMapLayerControl from "../components/Map/BaseMapLayerControl";
import FullscreenControlCustom from "../components/Map/FullscreenControlCustom";
import { BaseMapValue } from "../config/maps/baseMaps";
import { toTimestamp } from "../utilities/functions";
import { useTranslate } from "../hooks/LanguageProvider";
import RouteSimulator from "../components/Map/RouteSimulator";

type Trip = {
  id_trip: number;
  psn?: string;
  odoo_slip_id: number;
  delivery_ref: string;
  truck_plate: string;
  driver_name: string;
  departure: string;
  destination: string;
  created_at: string;
  status: string;
  duration_stop: number;
  webhook_state?: string;
  webhook_attempts?: number;
  webhook_http_status?: number;
  webhook_error?: string;
  webhook_delivered_at?: string;
  stationnements?: Array<{
    event_time: string;
    duration: number;
    latitude: number;
    longitude: number;
  }>;
};

type Position = {
  psn?: string;
  lat: number;
  lng: number;
  event_time: string;
  speed?: number;
};
type ItineraryPoint = {
  LAT?: number;
  LNG?: number;
  LON?: number;
  SOG?: number;
  ENGINESTAT?: number;
  TIMESTAMP?: string;
  TIMESTAMP_END?: string;
};
type TripDetail = Trip & {
  webhook?: {
    state?: string;
    attempts?: number;
    http_status?: number;
    error?: string;
  };
};
type SearchType =
  | "Trip ID"
  | "BL Odoo"
  | "Référence livraison"
  | "Véhicule"
  | "Conducteur"
  | "Trajet"
  | "Statut";

const searchTypes: SearchType[] = [
  "Trip ID",
  "BL Odoo",
  "Référence livraison",
  "Véhicule",
  "Conducteur",
  "Trajet",
  "Statut",
];
const searchTypeToApi: Record<SearchType, string> = {
  "Trip ID": "trip_id",
  "BL Odoo": "odoo_slip_id",
  "Référence livraison": "delivery_ref",
  Véhicule: "vehicle",
  Conducteur: "driver",
  Trajet: "route",
  Statut: "status",
};
const searchLabelKeys: Record<SearchType, string> = {
  "Trip ID": "Trip ID",
  "BL Odoo": "BL Odoo",
  "Référence livraison": "Delivery reference",
  Véhicule: "Vehicle",
  Conducteur: "Driver",
  Trajet: "Route",
  Statut: "Status",
};
const statusVariant: Record<string, string> = {
  in_progress: "warning",
  arrived: "info",
  delayed: "danger",
};
const webhookVariant: Record<string, string> = {
  pending: "secondary",
  retry: "warning",
  review: "danger",
  delivered: "info",
};

const itineraryCoordinates = (
  point: ItineraryPoint,
): [number, number] | null => {
  const latitude = Number(point.LAT);
  const longitude = Number(point.LNG ?? point.LON);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? [latitude, longitude]
    : null;
};

function FitItineraryBounds({ path }: { path: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (path.length > 0)
      map.fitBounds(L.latLngBounds(path), { padding: [24, 24] });
  }, [map, path]);
  return null;
}

function ItineraryPointsToggle({
  pointCount,
  showPoints,
  onTogglePoints,
}: {
  pointCount: number;
  showPoints: boolean;
  onTogglePoints: () => void;
}) {
  return (
    <div
      className="d-flex align-items-center bg-white rounded shadow-sm p-2"
      style={{
        position: "absolute",
        top: "12px",
        left: "12px",
        zIndex: 1000,
        gap: "6px",
      }}
    >
      <span className="small text-muted mr-2">{pointCount} point(s)</span>
      <button
        type="button"
        className={`btn btn-sm ${showPoints ? "btn-primary" : "btn-light"}`}
        title={showPoints ? "Masquer les points" : "Afficher les points"}
        onClick={onTogglePoints}
      >
        <i className="las la-dot-circle" />
      </button>
    </div>
  );
}

const itineraryMarker = (color: string) =>
  L.divIcon({
    className: "odoo-itinerary-marker",
    html: `<span style="display:block;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px #333"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

export function OdooTrips() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { translate } = useTranslate();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeSearch, setTypeSearch] = useState<SearchType>("Trip ID");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [column, setColumn] = useState("created_at");
  const [sort, setSort] = useState("DESC");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TripDetail | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [itineraryPoints, setItineraryPoints] = useState<ItineraryPoint[]>([]);
  const [itineraryLoading, setItineraryLoading] = useState(false);
  const [itineraryError, setItineraryError] = useState(false);
  const [showItineraryPoints, setShowItineraryPoints] = useState(false);
  const [stationnementsVisible, setStationnementsVisible] = useState(false);
  const [baseMap, setBaseMap] = useState<BaseMapValue>("google_roadmap");
  const [detailLoading, setDetailLoading] = useState(false);
  const latestRequest = useRef(0);
  const [selectedColumns, setSelectedColumns] = useState({
    id_trip: true,
    odoo_slip_id: true,
    delivery_ref: true,
    vehicle: true,
    route: true,
    created_at: true,
    duration_stop: true,
    status: true,
    webhook: true,
    infos: true,
  });

  const getTrips = async (
    nextLimit: number,
    nextPage: number,
    nextSearch: string,
    nextType: SearchType,
    nextStatus: string,
    nextDateFrom: string,
    nextDateTo: string,
    nextColumn: string,
    nextSort: string,
    showLoader = true,
  ) => {
    const requestId = ++latestRequest.current;
    if (showLoader) setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/trip/search`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          limitValue: nextLimit,
          currentPage: nextPage,
          search: nextSearch,
          searchType: searchTypeToApi[nextType],
          status: nextStatus,
          dateFrom: nextDateFrom,
          dateTo: nextDateTo,
          column: nextColumn,
          sort: nextSort,
        }),
      });
      if (!response.ok) throw new Error("Unable to retrieve Odoo trips");
      const payload = await response.json();
      if (requestId !== latestRequest.current) return;
      setTrips(payload.data || []);
      setTotal(Number(payload.total || 0));
    } catch (error) {
      if (requestId === latestRequest.current) console.error(error);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    void getTrips(
      limit,
      currentPage,
      search,
      typeSearch,
      status,
      dateFrom,
      dateTo,
      column,
      sort,
    );
    return () => {
      latestRequest.current += 1;
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(
      () =>
        void getTrips(
          limit,
          currentPage,
          search,
          typeSearch,
          status,
          dateFrom,
          dateTo,
          column,
          sort,
          false,
        ),
      30000,
    );
    return () => window.clearInterval(interval);
  }, [
    limit,
    currentPage,
    search,
    typeSearch,
    status,
    dateFrom,
    dateTo,
    column,
    sort,
  ]);

  const reload = (nextPage = currentPage) =>
    getTrips(
      limit,
      nextPage,
      search,
      typeSearch,
      status,
      dateFrom,
      dateTo,
      column,
      sort,
    );
  const handlePageClick = ({ selected }: { selected: number }) => {
    const nextPage = selected + 1;
    setCurrentPage(nextPage);
    void reload(nextPage);
    window.scrollTo(0, 0);
  };
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value);
    setLimit(value);
    setCurrentPage(1);
    void getTrips(
      value,
      1,
      search,
      typeSearch,
      status,
      dateFrom,
      dateTo,
      column,
      sort,
    );
  };
  const handleAdvancedSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    setCurrentPage(1);
    void getTrips(
      limit,
      1,
      value,
      typeSearch,
      status,
      dateFrom,
      dateTo,
      column,
      sort,
    );
  };
  const handleTypeSearch = (eventKey: string | null) => {
    if (!eventKey || !searchTypes.includes(eventKey as SearchType)) return;
    const value = eventKey as SearchType;
    setTypeSearch(value);
    setCurrentPage(1);
    void getTrips(
      limit,
      1,
      search,
      value,
      status,
      dateFrom,
      dateTo,
      column,
      sort,
    );
  };
  const handleStatus = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setStatus(value);
    setCurrentPage(1);
    void getTrips(
      limit,
      1,
      search,
      typeSearch,
      value,
      dateFrom,
      dateTo,
      column,
      sort,
    );
  };
  const handleDateChange = (name: "from" | "to", value: string) => {
    const nextFrom = name === "from" ? value : dateFrom;
    const nextTo = name === "to" ? value : dateTo;
    if (nextFrom && nextTo && nextFrom > nextTo) return;
    if (name === "from") setDateFrom(value);
    else setDateTo(value);
    setCurrentPage(1);
    void getTrips(
      limit,
      1,
      search,
      typeSearch,
      status,
      nextFrom,
      nextTo,
      column,
      sort,
    );
  };
  const handleResetSearch = () => {
    setSearch("");
    setTypeSearch("Trip ID");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    void getTrips(limit, 1, "", "Trip ID", "", "", "", column, sort);
  };
  const handleSortingColumn = (nextColumn: string) => {
    const nextSort = sort === "ASC" ? "DESC" : "ASC";
    setColumn(nextColumn);
    setSort(nextSort);
    setCurrentPage(1);
    void getTrips(
      limit,
      1,
      search,
      typeSearch,
      status,
      dateFrom,
      dateTo,
      nextColumn,
      nextSort,
    );
  };
  const handleColumnChange = (name: keyof typeof selectedColumns) =>
    setSelectedColumns((previous) => ({
      ...previous,
      [name]: !previous[name],
    }));

  const openDetail = async (trip: Trip) => {
    setSelected(trip);
    setPositions([]);
    setItineraryPoints([]);
    setItineraryError(false);
    setShowItineraryPoints(false);
    setStationnementsVisible(false);
    setDetailLoading(true);
    try {
      const [detailResponse, positionsResponse] = await Promise.all([
        fetch(`${backendUrl}/api/trip/detail/${trip.id_trip}`, {
          credentials: "include",
        }),
        fetch(`${backendUrl}/api/trip/detail/${trip.id_trip}/positions`, {
          credentials: "include",
        }),
      ]);
      if (!detailResponse.ok || !positionsResponse.ok)
        throw new Error("Unable to retrieve trip detail");
      const detail = await detailResponse.json();
      const route = await positionsResponse.json();
      const loadedPositions = route.data || [];
      setSelected(detail.data);
      setPositions(loadedPositions);
      if (detail.data?.psn && loadedPositions.length > 0) {
        const fromDate =
          loadedPositions[0].event_time || detail.data.created_at;
        const toDate =
          loadedPositions[loadedPositions.length - 1].event_time ||
          new Date().toISOString();
        setItineraryLoading(true);
        void (async () => {
          try {
            const reportResponse = await fetch(
              `${backendUrl}/api/map/reports/date/1/${encodeURIComponent(detail.data.psn)}/${encodeURIComponent(fromDate)}/${encodeURIComponent(toDate)}`,
            );
            if (!reportResponse.ok)
              throw new Error("Unable to retrieve itinerary");
            const report = await reportResponse.json();
            setItineraryPoints(Array.isArray(report.data) ? report.data : []);
          } catch (error) {
            console.error(error);
            setItineraryError(true);
            setItineraryPoints([]);
          } finally {
            setItineraryLoading(false);
          }
        })();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (value?: string) =>
    value ? toTimestamp(value).replace(/:\d{2}$/, "") : "—";
  const searchLabel = (value: SearchType) => translate(searchLabelKeys[value]);
  const statusLabel = (value: string) =>
    value === "in_progress"
      ? translate("In progress")
      : value === "arrived"
        ? translate("Arrived")
        : value === "delayed"
          ? translate("Delayed")
          : value;
  const statusBadge = (value?: string) => (
    <span
      className={`badge badge-${statusVariant[value || ""] || "secondary"}`}
    >
      {value ? statusLabel(value) : "—"}
    </span>
  );
  const webhookLabel = (value?: string) =>
    value === "pending"
      ? translate("Webhook pending")
      : value === "retry"
        ? translate("Webhook retry")
        : value === "review"
          ? translate("Webhook review")
          : value === "delivered"
            ? translate("Webhook delivered")
            : translate("Webhook not triggered");
  const webhookBadge = (value?: string, attempts?: number) => (
    <span
      className={`badge badge-${webhookVariant[value || ""] || "secondary"}`}
    >
      {webhookLabel(value)}
      {value ? ` · ${attempts || 0}` : ""}
    </span>
  );
  const visibleColumns = Object.values(selectedColumns).filter(Boolean).length;
  const pageCount = Math.ceil(total / limit);
  const itineraryPath = useMemo(
    () =>
      itineraryPoints
        .map(itineraryCoordinates)
        .filter((point): point is [number, number] => point !== null),
    [itineraryPoints],
  );
  const itineraryStops = itineraryPoints.filter((point, index) => {
    if (index === 0 || index === itineraryPoints.length - 1) return false;
    const speed = Number(point.SOG);
    const previousSpeed = Number(itineraryPoints[index - 1].SOG);
    const nextSpeed = Number(itineraryPoints[index + 1].SOG);
    return (
      speed <= 0.3 &&
      previousSpeed <= 0.3 &&
      nextSpeed <= 0.3 &&
      index % 10 === 0
    );
  });
  const parkingCount =
    selected?.stationnements?.length || itineraryStops.length;
  const columnLabels = {
    id_trip: translate("Trip ID"),
    odoo_slip_id: translate("BL Odoo"),
    delivery_ref: translate("Delivery reference"),
    vehicle: translate("Vehicle"),
    route: translate("Route"),
    created_at: translate("Date de création"),
    duration_stop: translate("Stop threshold"),
    status: translate("Status"),
    webhook: translate("Webhook"),
    infos: translate("Infos"),
  };

  return (
    <>
      <div className="row odoo-trips-header align-items-center mb-2">
        <div className="col-md-6 col-sm-12">
          <h4 className="mb-0">
            <i className="las la-route mr-2" />
            {translate("Trips Odoo")} ({total})
          </h4>
        </div>
        <div className="col-md-6 col-sm-12 text-right">
          <button
            type="button"
            className="btn btn-outline-info mr-1"
            onClick={() => void reload()}
          >
            <i className="las la-sync mr-2" />
            {translate("Refresh")}
          </button>
        </div>
      </div>
      <div className="row odoo-trips-toolbar">
        <div
          className="col-xl-3 col-md-4 col-12 list-search-column"
          style={{ margin: "0px 0px 10px 0px", padding: "10px", minWidth: 0 }}
        >
          <ListSearchBar
            criteria={
              <Dropdown onSelect={handleTypeSearch}>
                <Dropdown.Toggle variant="link" id="odoo-trip-search-type">
                  {searchLabel(typeSearch)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {searchTypes.map((value) => (
                    <Dropdown.Item eventKey={value} key={value}>
                      {searchLabel(value)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            }
            onReset={handleResetSearch}
            resetLabel={translate("Reset search")}
          >
            <input
              type="text"
              placeholder={`${translate("Search by")} ${searchLabel(typeSearch)}`}
              value={search}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
          </ListSearchBar>
        </div>
        <div
          className="col-xl-9 col-md-8 col-12 d-flex justify-content-end align-items-center flex-wrap"
          style={{ gap: "8px" }}
        >
          <div className="mb-2">
            <label className="mb-0 small">
              {translate("From date")}
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(event) =>
                  handleDateChange("from", event.target.value)
                }
                className="form-control form-control-sm ml-1 d-inline-block"
                style={{ width: "135px" }}
              />
            </label>
          </div>
          <div className="mb-2">
            <label className="mb-0 small">
              {translate("To date")}
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(event) => handleDateChange("to", event.target.value)}
                className="form-control form-control-sm ml-1 d-inline-block"
                style={{ width: "135px" }}
              />
            </label>
          </div>
          <select
            value={status}
            className="custom-select custom-select-sm mb-2"
            style={{ width: "180px" }}
            onChange={handleStatus}
            aria-label={translate("Status")}
          >
            <option value="">{translate("All statuses")}</option>
            <option value="in_progress">{translate("In progress")}</option>
            <option value="arrived">{translate("Arrived")}</option>
            <option value="delayed">{translate("Delayed")}</option>
          </select>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              id="odoo-trip-columns"
              title={translate("Column display")}
            >
              <i className="las la-eye" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.entries(columnLabels).map(([key, label]) => (
                <Dropdown.Item
                  as="div"
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "default",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      selectedColumns[key as keyof typeof selectedColumns]
                    }
                    onClick={(event) => event.stopPropagation()}
                    onChange={() =>
                      handleColumnChange(key as keyof typeof selectedColumns)
                    }
                  />
                  <span style={{ marginLeft: "10px" }}>{label}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <div className="dataTables_length mb-2 ml-1" id="OdooTrips_length">
            <select
              value={limit}
              name="OdooTrips_length"
              aria-label={translate("Rows count")}
              className="custom-select custom-select-sm form-control form-control-sm"
              style={{ width: "66px" }}
              onChange={handleSelectChange}
            >
              {[10, 20, 50, 100, 200, 500].map((value) => (
                <option value={value} key={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="row m-1">
        <div className="table-responsive">
          <Table className="dataTable">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="checkbox" />
                  </div>
                </th>
                {selectedColumns.id_trip && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("id_trip")}
                  >
                    Trip ID
                  </th>
                )}
                {selectedColumns.odoo_slip_id && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("odoo_slip_id")}
                  >
                    BL Odoo
                  </th>
                )}
                {selectedColumns.delivery_ref && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("delivery_ref")}
                  >
                    Référence livraison
                  </th>
                )}
                {selectedColumns.vehicle && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("truck_plate")}
                  >
                    Véhicule
                  </th>
                )}
                {selectedColumns.route && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("destination")}
                  >
                    Trajet
                  </th>
                )}
                {selectedColumns.created_at && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("created_at")}
                  >
                    Date De Création
                  </th>
                )}
                {selectedColumns.duration_stop && <th>Seuil arrêt</th>}
                {selectedColumns.status && (
                  <th
                    className="sorting"
                    onClick={() => handleSortingColumn("status")}
                  >
                    Statut
                  </th>
                )}
                {selectedColumns.webhook && <th>Webhook</th>}
                {selectedColumns.infos && <th>Infos</th>}
              </tr>
            </thead>
            <tbody className="ligth-body">
              {loading ? (
                <tr>
                  <td className="p-0" colSpan={visibleColumns + 1}>
                    <div
                      className="d-flex justify-content-center align-items-center w-100"
                      style={{ minHeight: "96px" }}
                      role="status"
                      aria-label="Chargement des trips"
                    >
                      <PropagateLoader
                        color="#123abc"
                        loading={loading}
                        size={20}
                      />
                    </div>
                  </td>
                </tr>
              ) : trips.length === 0 ? (
                <tr>
                  <td
                    className="text-center text-muted py-4"
                    colSpan={visibleColumns + 1}
                  >
                    Aucun trip Odoo disponible
                  </td>
                </tr>
              ) : (
                trips.map((trip) => (
                  <tr key={trip.id_trip}>
                    <td>
                      <div className="form-check form-check-inline">
                        <input type="checkbox" className="form-check-input" />
                      </div>
                    </td>
                    {selectedColumns.id_trip && <td>{trip.id_trip}</td>}
                    {selectedColumns.odoo_slip_id && (
                      <td>{trip.odoo_slip_id}</td>
                    )}
                    {selectedColumns.delivery_ref && (
                      <td>
                        {trip.delivery_ref || "—"}
                        <small className="d-block text-muted">
                          Créé le {formatDate(trip.created_at)}
                        </small>
                      </td>
                    )}
                    {selectedColumns.vehicle && (
                      <td>
                        {trip.truck_plate}
                        <small className="d-block text-muted">
                          {trip.driver_name}
                        </small>
                      </td>
                    )}
                    {selectedColumns.route && (
                      <td>
                        {trip.departure}{" "}
                        <i className="las la-arrow-right mx-1" />{" "}
                        {trip.destination}
                      </td>
                    )}
                    {selectedColumns.created_at && (
                      <td>{formatDate(trip.created_at)}</td>
                    )}
                    {selectedColumns.duration_stop && (
                      <td>{trip.duration_stop} s</td>
                    )}
                    {selectedColumns.status && (
                      <td>{statusBadge(trip.status)}</td>
                    )}
                    {selectedColumns.webhook && (
                      <td>
                        {webhookBadge(
                          trip.webhook_state,
                          trip.webhook_attempts,
                        )}
                        {trip.webhook_error && (
                          <small className="d-block text-danger">
                            {trip.webhook_error}
                          </small>
                        )}
                      </td>
                    )}
                    {selectedColumns.infos && (
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          title="Ouvrir le trip"
                          onClick={() => void openDetail(trip)}
                        >
                          <i
                            className="las la-route"
                            style={{ fontSize: "1.2em" }}
                          />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {loading
              ? "Chargement…"
              : trips.length === 0
                ? "Aucun trip Odoo disponible"
                : `Affichage de ${trips.length} sur ${total}`}
          </span>
        </div>
        <div className="col-md-6">
          {pageCount > 0 && (
            <ReactPaginate
              previousLabel="previous"
              nextLabel="next"
              breakLabel="..."
              pageCount={pageCount}
              forcePage={Math.min(currentPage - 1, pageCount - 1)}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName="pagination justify-content-center"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakClassName="page-item"
              breakLinkClassName="page-link"
              activeClassName="active"
            />
          )}
        </div>
      </div>
      <Offcanvas
        show={Boolean(selected)}
        onHide={() => {
          setSelected(null);
          setPositions([]);
          setItineraryPoints([]);
          setShowItineraryPoints(false);
          setStationnementsVisible(false);
          setItineraryLoading(false);
          setItineraryError(false);
        }}
        placement="end"
        style={{ width: "70vw" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Trip Odoo #{selected?.id_trip}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selected && (
            <>
              {detailLoading && (
                <div
                  className="small text-muted mb-2"
                  role="status"
                  aria-label="Chargement des détails du trip"
                >
                  Chargement des détails…
                </div>
              )}
              <div className="row mb-3">
                <div className="col-6">
                  <small className="text-muted">BL Odoo</small>
                  <div>{selected.odoo_slip_id}</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Statut</small>
                  <div>{statusBadge(selected.status)}</div>
                </div>
                <div className="col-6 mt-3">
                  <small className="text-muted">Véhicule</small>
                  <div>{selected.truck_plate}</div>
                </div>
                <div className="col-6 mt-3">
                  <small className="text-muted">Seuil arrêt</small>
                  <div>{selected.duration_stop} s</div>
                </div>
              </div>
              <div className="border rounded p-3 mb-3">
                <h6>Notification Odoo</h6>
                {webhookBadge(
                  selected.webhook?.state || selected.webhook_state,
                  selected.webhook?.attempts || selected.webhook_attempts,
                )}
                <div className="small mt-2">
                  HTTP{" "}
                  {selected.webhook?.http_status ||
                    selected.webhook_http_status ||
                    "—"}
                </div>
                {selected.webhook?.error || selected.webhook_error ? (
                  <div className="small text-danger mt-1">
                    {selected.webhook?.error || selected.webhook_error}
                  </div>
                ) : null}
              </div>
              <div className="border rounded p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Reconstitution itinéraire</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={parkingCount === 0}
                    onClick={() =>
                      setStationnementsVisible((current) => !current)
                    }
                  >
                    <i className="las la-parking mr-1" />
                    Stationnements ({parkingCount})
                  </button>
                </div>
                <p className="mb-2">
                  Les positions GPS sont disponibles : {itineraryPath.length}{" "}
                  point(s).
                </p>
                {itineraryLoading ? (
                  <div className="text-muted py-3">
                    Chargement de l'itinéraire…
                  </div>
                ) : itineraryPath.length === 0 ? (
                  <div className="text-muted py-3">
                    {itineraryError
                      ? "Itinéraire indisponible"
                      : "Aucune donnée d'itinéraire"}
                  </div>
                ) : (
                  <div
                    style={{
                      height: "min(68vh, 760px)",
                      minHeight: "460px",
                      width: "100%",
                    }}
                  >
                    <MapContainer
                      center={itineraryPath[0]}
                      zoom={8}
                      scrollWheelZoom
                      style={{ height: "100%", width: "100%" }}
                    >
                      <BaseMapLayerControl
                        value={baseMap}
                        onChange={setBaseMap}
                        top={12}
                        right={12}
                      />
                      <FullscreenControlCustom position="topright" />
                      <ItineraryPointsToggle
                        pointCount={itineraryPath.length}
                        showPoints={showItineraryPoints}
                        onTogglePoints={() =>
                          setShowItineraryPoints((current) => !current)
                        }
                      />
                      <FitItineraryBounds path={itineraryPath} />
                      <Polyline
                        positions={itineraryPath}
                        pathOptions={{ color: "#00aaff", weight: 4 }}
                      />
                      <RouteSimulator
                        path={itineraryPath}
                        pointSpeeds={itineraryPoints.map(
                          (point) => Number(point.SOG) || 0,
                        )}
                        speed={70}
                        autofit={false}
                        right
                        iconSize={30}
                      />
                      {showItineraryPoints && (
                        <MarkerClusterGroup chunkedLoading>
                          {itineraryPath.map((point, index) => (
                            <Marker
                              key={`point-${index}`}
                              position={point}
                              icon={itineraryMarker("#6c757d")}
                            >
                              <Popup>Point GPS {index + 1}</Popup>
                            </Marker>
                          ))}
                        </MarkerClusterGroup>
                      )}
                      {itineraryStops.map((stop, index) => {
                        const coordinates = itineraryCoordinates(stop);
                        if (!coordinates) return null;
                        return (
                          <Marker
                            key={`${stop.TIMESTAMP || "stop"}-${index}`}
                            position={coordinates}
                            icon={itineraryMarker("#f0ad4e")}
                          >
                            <Popup>
                              <strong>Stationnement</strong>
                              <br />
                              {formatDate(stop.TIMESTAMP)}
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  </div>
                )}
                {stationnementsVisible && (
                  <div className="mt-3 pt-3 border-top">
                    <div className="small font-weight-bold mb-2">
                      Liste des stationnements
                    </div>
                    {(selected.stationnements || []).length > 0 ? (
                      <Table size="sm" responsive className="mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Durée</th>
                            <th>Position</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selected.stationnements?.map((stop, index) => (
                            <tr key={`${stop.event_time}-${index}`}>
                              <td>{formatDate(stop.event_time)}</td>
                              <td>{stop.duration} s</td>
                              <td>
                                {stop.latitude}, {stop.longitude}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="small text-muted">
                        Les stationnements sont visibles sur la carte.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
